import React, { useContext, useCallback, useMemo, useState, useEffect, useRef, MutableRefObject } from 'react';
import { IEditorProps } from '../Editor';
import { EditorLabel } from '../EditorLabel';
import { EditorConfigButtons } from '../EditorConfigButtons';
import { TextField } from 'components/inputs/TextField';
import { EditorConfigButton } from '../EditorConfigButton';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { useEditorId } from '../hooks/useEditorId';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { addInput } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/Mapping/MappingDataService';
import { AddInputParams } from '@decisions/server/DecisionsFramework/Design/Flow/Service/Edit/Mapping/AddInputParams';
import { Callout } from './Callout';
import { Search } from './Search';
import { MappingContextType } from '@decisions/server/DecisionsFramework/Design/Flow/Service/MappingContextType';
import './DataDescriptionPathEditor.less';
import { EditorValidationWrapper } from '../EditorValidationWrapper';
import { getEditorValidationSummary } from 'utils/ValidationUtils';

interface IDataDescriptionPathEditorProps extends IEditorProps {}

/**
 * Select DataDescriptionPathEditor Editor.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function DataDescriptionPathEditor({
  child,
  path,
  onChange,
  hideInputLabel,
  showDialogOnRender,
}: IDataDescriptionPathEditorProps) {
  const context = useContext(PropertyEditorContext);
  const [id] = useEditorId(path);
  const [displayText, setDisplayText] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const hasShownDialog = useRef(false);

  const linkRef: MutableRefObject<HTMLSpanElement | null> = useRef<HTMLSpanElement>(null);

  const isInput: boolean = useMemo(() => {
    return getEditorConfigValue(child, 'type') === 'input';
  }, [child]);

  const canAddInput = useMemo(() => {
    const contextType = context.mappingContext.Type;
    return (
      !!getEditorConfigValue(child, 'canAddInput') &&
      (contextType === MappingContextType.Flow || contextType === MappingContextType.Rule)
    );
  }, [child, context.mappingContext]);

  const inputDataName = useMemo(() => {
    return getEditorConfigValue(child, 'inputDataName');
  }, [child]);

  const isEditableAsString: boolean = useMemo(() => {
    return !!getEditorConfigValue(child, 'IsEditableAsString');
  }, [child]);

  const tooglePopup = useCallback(() => {
    setPopupOpen(!popupOpen);
  }, [popupOpen, setPopupOpen]);

  const closePopup = useCallback(() => {
    setPopupOpen(false);
  }, [setPopupOpen]);

  const onReset = useCallback(() => {
    onChange(null, path);
  }, [onChange, path]);

  const addInputClick = useCallback(
    (result: string) => {
      addInput({
        param: {
          MappingContext: context.mappingContext,
          EditingContext: context.editingContextId,
          EditorPath: path,
          StepInputName: inputDataName,
          NewInputName: result,
        } as AddInputParams,
      });
    },
    [context.editingContextId, context.mappingContext, inputDataName, path]
  );

  useEffect(() => {
    setDisplayText(child.Value.OutputValue);
  }, [child]);

  const onValueChange = useCallback((value: string) => {
    setDisplayText(value);
  }, []);

  const onShowPicker = useCallback(() => {
    context.onShowDialogue({
      node: child,
      editingContextId: context.editingContextId,
      path,
      name: 'ShowDataDescriptionPicker',
      options: {
        isInput,
        onPicked: onValueChange,
        onChange: (value: string) => {
          onChange(value, path);
        },
      },
    });
  }, [context, isInput, child, path, onValueChange, onChange]);

  const onShowEditor = useCallback(() => {
    context.onShowDialogue({
      node: child,
      path,
      editingContextId: context.editingContextId,
      name: 'ShowDataDescriptionEditor',
      options: {
        onPicked: onValueChange,
        propertyPath: path,
        serverContext: context.mappingContext,
        isInput,
      },
    });
  }, [child, context, isInput, onValueChange, path]);

  useEffect(() => {
    if (showDialogOnRender && hasShownDialog.current === false) {
      hasShownDialog.current = true;
      onShowEditor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDialogOnRender]);

  return (
    <EditorLabel htmlFor={id} editor={child} path={path} doNotWrap hideInputLabel={hideInputLabel}>
      <div id={id} className="dpc-ddpath-editor">
        {isEditableAsString && isInput && (
          <EditorValidationWrapper editor={child}>
            <Search displayText={displayText} path={path} child={child} />
          </EditorValidationWrapper>
        )}
        {!(isEditableAsString && isInput) && (
          <TextField
            validation={getEditorValidationSummary(child)}
            disabled
            onClear={onReset}
            showClear
            value={displayText}
          />
        )}
        <EditorConfigButtons>
          {!isInput && (
            <EditorConfigButton className="dpc-ddpath-editor__button" onClick={onShowPicker}>
              Pick
            </EditorConfigButton>
          )}
          {canAddInput && (
            <EditorConfigButton className="dpc-ddpath-editor__button" onClick={tooglePopup}>
              <span ref={linkRef}>Add Input</span>
            </EditorConfigButton>
          )}
          {isInput && (
            <EditorConfigButton className="dpc-ddpath-editor__button" onClick={onShowEditor}>
              Edit
            </EditorConfigButton>
          )}
        </EditorConfigButtons>

        {popupOpen && (
          <Callout popupOpen={popupOpen} closePopup={closePopup} linkRef={linkRef} onSubmit={addInputClick} />
        )}
      </div>
    </EditorLabel>
  );
}
